/**
 * UserGroupsWorkflowScreen — User Group management
 * Layout: ERPAdvancedGrid (left) + Detail / Members panel (right) + context menu
 */
import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, getToken } from '../../lib/api';
import { Users, Save, Trash2, X, Edit2, UserCheck, Plus } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import { erpToast } from '../../components/erp/ERPToast';

function authFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers || {}),
    },
  });
}

const GROUP_COLS = [
  { field: 'UGroupId',          header: 'Group ID',    frozen: true  },
  { field: 'UGroupName',        header: 'Name',        frozen: false },
  { field: 'UGroupDescription', header: 'Description', frozen: false },
  { field: 'UserCount',         header: 'Members',     frozen: false },
  { field: 'UActiveLabel',      header: 'Active',      frozen: false },
];

const EMPTY_FORM = { UGroupName: '', UGroupDescription: '', UActive: true };

export default function UserGroupsWorkflowScreen({ theme, onBack }) {
  const isDark = theme === 'dark';

  const [groups,      setGroups]      = useState([]);
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isNew,       setIsNew]       = useState(false);
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [activeTab,   setActiveTab]   = useState('details');
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(20);
  const [sortField,   setSortField]   = useState('UGroupId');
  const [sortDir,     setSortDir]     = useState('asc');
  const [filters,     setFilters]     = useState({});
  const [ctxMenu,     setCtxMenu]     = useState(null);
  const ctxRef = useRef(null);

  const panel = isDark ? '#161b27' : '#ffffff';
  const bd    = isDark ? '#1e2533' : '#e2e8f0';
  const tx    = isDark ? '#e2e8f0' : '#1e293b';
  const mt    = isDark ? '#64748b' : '#94a3b8';
  const ac    = '#6366f1';

  // ── load helpers (before useEffect) ──────────────────────────────────────────
  // refreshGroups is called from handleSave/handleDelete (click handlers only, NOT from useEffect)
  const refreshGroups = async () => {
    setLoading(true);
    try {
      const d = await authFetch('/api/uams/groups').then(r => r.json());
      if (d.success) {
        setGroups((d.data || []).map(g => ({ ...g, UActiveLabel: g.UActive ? 'Yes' : 'No' })));
      }
    } catch (_) { erpToast.error('Failed to reload groups'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!formData.UGroupName.trim()) { erpToast.error('Group Name is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const d = await authFetch('/api/uams/groups', {
          method: 'POST',
          body: JSON.stringify({ UGroupName: formData.UGroupName, UGroupDescription: formData.UGroupDescription }),
        }).then(r => r.json());
        if (d.success) {
          erpToast.success(`Group "${formData.UGroupName}" created`);
          setIsNew(false);
          setFormData(EMPTY_FORM);
          await refreshGroups();
        } else { erpToast.error(d.error || 'Create failed'); }
      } else {
        const d = await authFetch(`/api/uams/groups/${selectedRow.UGroupId}`, {
          method: 'PUT',
          body: JSON.stringify({
            UGroupName: formData.UGroupName,
            UGroupDescription: formData.UGroupDescription,
            UActive: formData.UActive,
          }),
        }).then(r => r.json());
        if (d.success) {
          erpToast.success('Group saved');
          await refreshGroups();
        } else { erpToast.error(d.error || 'Save failed'); }
      }
    } catch (_) { erpToast.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete group "${group.UGroupName}"?\nThis action cannot be undone.`)) return;
    try {
      const d = await authFetch(`/api/uams/groups/${group.UGroupId}`, { method: 'DELETE' }).then(r => r.json());
      if (d.success) {
        erpToast.success('Group deleted');
        if (selectedRow?.UGroupId === group.UGroupId) { setSelectedRow(null); setFormData(EMPTY_FORM); }
        await refreshGroups();
      } else { erpToast.error(d.error || 'Delete failed'); }
    } catch (_) { erpToast.error('Network error'); }
  };

  // ── useEffect — initial load only, inline async per project ESLint pattern ────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [gRes, uRes] = await Promise.all([
          authFetch('/api/uams/groups').then(r => r.json()),
          authFetch('/api/uams/users').then(r => r.json()),
        ]);
        if (gRes.success) setGroups((gRes.data || []).map(g => ({ ...g, UActiveLabel: g.UActive ? 'Yes' : 'No' })));
        if (uRes.success) setUsers(uRes.data || []);
      } catch (_) { erpToast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    init();
  }, []);

  // Outside-click dismisses context menu — stable effect with empty deps (ctxRef guards null)
  useEffect(() => {
    const handler = (e) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target)) setCtxMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── helpers ───────────────────────────────────────────────────────────────────
  const openEdit = (row) => {
    setIsNew(false);
    setSelectedRow(row);
    setFormData({ UGroupName: row.UGroupName, UGroupDescription: row.UGroupDescription || '', UActive: row.UActive !== false });
    setActiveTab('details');
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    const tr = e.target.closest('tr[data-testid]');
    if (!tr) return;
    const testId = tr.getAttribute('data-testid');
    const parts = testId.split('-row-');
    if (parts.length < 2) return;
    const rowId = parts[1];
    const row = groups.find(g => g.UGroupId === rowId);
    if (row) setCtxMenu({ x: e.clientX, y: e.clientY, row });
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  // ── derived data ──────────────────────────────────────────────────────────────
  const filtered = groups.filter(g =>
    Object.entries(filters).every(([k, v]) => !v || String(g[k] || '').toLowerCase().includes(v.toLowerCase()))
  );
  const sorted = [...filtered].sort((a, b) => {
    const cmp = String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);
  const groupMembers = users.filter(u => u.GroupId === selectedRow?.UGroupId);
  const panelOpen = isNew || !!selectedRow;

  return (
    <div
      data-testid="user-groups-screen"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      onContextMenu={handleContextMenu}
    >
      {/* Context Menu */}
      {ctxMenu && (
        <div
          ref={ctxRef}
          data-testid="groups-ctx-menu"
          style={{
            position: 'fixed', top: ctxMenu.y, left: ctxMenu.x,
            zIndex: 9999, background: panel, border: `1px solid ${bd}`,
            borderRadius: 6, boxShadow: '0 4px 20px rgba(0,0,0,.18)',
            minWidth: 160, overflow: 'hidden',
          }}
        >
          <div
            data-testid="ctx-edit-group"
            onClick={() => { openEdit(ctxMenu.row); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: tx }}
            onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = isDark ? '#1e2533' : '#f1f5f9'; }}
            onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          >
            <Edit2 size={12} color={ac} /> Edit Group
          </div>
          <div
            data-testid="ctx-view-members"
            onClick={() => { setSelectedRow(ctxMenu.row); setActiveTab('members'); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: tx }}
            onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = isDark ? '#1e2533' : '#f1f5f9'; }}
            onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          >
            <UserCheck size={12} color="#10b981" /> View Members
          </div>
          <div style={{ borderTop: `1px solid ${bd}`, margin: '2px 0' }} />
          <div
            data-testid="ctx-delete-group"
            onClick={() => { handleDelete(ctxMenu.row); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: '#ef4444' }}
            onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          >
            <Trash2 size={12} /> Delete Group
          </div>
        </div>
      )}

      {/* Body: grid + panel */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Left: Grid */}
        <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
          <ERPAdvancedGrid
            title="User Groups"
            columns={GROUP_COLS}
            data={paginated}
            total={filtered.length}
            idField="UGroupId"
            nameField="UGroupName"
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={s => { setPageSize(s); setPage(1); }}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            filters={filters}
            onFilterChange={f => { setFilters(f); setPage(1); }}
            loading={loading}
            onAddNew={() => { setIsNew(true); setSelectedRow(null); setFormData(EMPTY_FORM); setActiveTab('details'); }}
            onEdit={openEdit}
            onVoid={handleDelete}
            testId="user-groups-grid"
            selectedRowId={selectedRow?.UGroupId}
            onRowClick={openEdit}
            onBack={onBack}
          />
        </div>

        {/* Right: Detail Panel */}
        {panelOpen && (
          <div
            data-testid="groups-detail-panel"
            style={{
              width: 360, flexShrink: 0,
              borderLeft: `1px solid ${bd}`, background: panel,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
          >
            {/* Panel header */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: `1px solid ${bd}`, background: isDark ? '#1a2035' : '#f8fafc', flexShrink: 0 }}>
              <Users size={14} color={ac} />
              <span style={{ fontWeight: 700, fontSize: 12, color: tx, marginLeft: 6, flex: 1 }}>
                {isNew ? 'New Group' : selectedRow?.UGroupName}
              </span>
              <button
                data-testid="close-group-panel"
                onClick={() => { setSelectedRow(null); setIsNew(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mt, padding: 2 }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Tabs */}
            {!isNew && (
              <div style={{ display: 'flex', borderBottom: `1px solid ${bd}`, flexShrink: 0 }}>
                {[{ key: 'details', label: 'Details' }, { key: 'members', label: `Members (${groupMembers.length})` }].map(t => (
                  <button
                    key={t.key}
                    data-testid={`group-tab-${t.key}`}
                    onClick={() => setActiveTab(t.key)}
                    style={{
                      flex: 1, padding: '7px 0', fontSize: 11, fontWeight: 700,
                      background: activeTab === t.key ? panel : (isDark ? '#1a2035' : '#f8fafc'),
                      color: activeTab === t.key ? ac : mt,
                      border: 'none', borderBottom: activeTab === t.key ? `2px solid ${ac}` : '2px solid transparent',
                      cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '.06em',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {/* Tab content */}
            <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
              {(isNew || activeTab === 'details') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {!isNew && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>GROUP ID</div>
                      <div style={{ padding: '5px 8px', background: isDark ? '#1e2533' : '#f8fafc', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, color: mt }}>
                        {selectedRow?.UGroupId}
                      </div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>GROUP NAME *</div>
                    <input
                      data-testid="group-name-input"
                      value={formData.UGroupName}
                      onChange={e => setFormData(p => ({ ...p, UGroupName: e.target.value }))}
                      placeholder="Enter group name"
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>DESCRIPTION</div>
                    <textarea
                      data-testid="group-desc-input"
                      value={formData.UGroupDescription}
                      onChange={e => setFormData(p => ({ ...p, UGroupDescription: e.target.value }))}
                      placeholder="Group description"
                      rows={3}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  {!isNew && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>ACTIVE</div>
                      <div style={{ display: 'flex', gap: 16 }}>
                        {[true, false].map(v => (
                          <label key={String(v)} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12 }}>
                            <input
                              type="radio"
                              name="uactive-group"
                              checked={formData.UActive === v}
                              onChange={() => setFormData(p => ({ ...p, UActive: v }))}
                              style={{ accentColor: ac }}
                            />
                            <span style={{ color: tx }}>{v ? 'Yes' : 'No'}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button
                      data-testid="save-group-btn"
                      onClick={handleSave}
                      disabled={saving}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 0', background: saving ? '#a5b4fc' : ac, color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}
                    >
                      <Save size={13} /> {saving ? 'Saving...' : (isNew ? 'Create Group' : 'Save Changes')}
                    </button>
                    <button
                      data-testid="cancel-group-btn"
                      onClick={() => { setSelectedRow(null); setIsNew(false); }}
                      style={{ padding: '7px 12px', background: 'transparent', border: `1px solid ${bd}`, borderRadius: 5, color: mt, fontSize: 12, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                  {!isNew && selectedRow && (
                    <button
                      data-testid="delete-group-btn"
                      onClick={() => handleDelete(selectedRow)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 0', background: 'transparent', border: `1px solid #fca5a5`, borderRadius: 5, color: '#ef4444', fontSize: 11, cursor: 'pointer', marginTop: 4 }}
                    >
                      <Trash2 size={12} /> Delete Group
                    </button>
                  )}
                </div>
              )}

              {!isNew && activeTab === 'members' && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    Users in &ldquo;{selectedRow?.UGroupName}&rdquo;
                  </div>
                  {groupMembers.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: mt, fontSize: 12, background: isDark ? '#1e2533' : '#f8fafc', borderRadius: 6 }}>
                      <Users size={24} color={bd} style={{ marginBottom: 8 }} />
                      <div>No users assigned to this group</div>
                    </div>
                  ) : groupMembers.map(u => (
                    <div
                      key={u.UserId}
                      data-testid={`group-member-${u.UserId}`}
                      style={{ display: 'flex', alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${bd}22` }}
                    >
                      <UserCheck size={12} color={ac} style={{ marginRight: 8, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: tx }}>{u.UserName}</div>
                        <div style={{ fontSize: 10, color: mt }}>{u.UserId} &middot; {u.RoleName || '—'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hint when panel closed */}
        {!panelOpen && !loading && (
          <div style={{ width: 200, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: mt, fontSize: 11, fontStyle: 'italic', borderLeft: `1px solid ${bd}`, background: isDark ? '#0f1117' : '#f8fafc' }}>
            <div style={{ textAlign: 'center', padding: 12 }}>
              <Plus size={20} color={bd} />
              <div style={{ marginTop: 6 }}>Click a row to edit or use Add New</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
